import React, { useCallback, useEffect, useRef } from 'react';
import PostItem from './PostItem';

const PostList = ({
    posts,
    loading,
    currentPage = 1,
    totalPages = 0,
    onPageChange,
    selectedTags,
    selectedTagSet,
    onSelectPost,
    onActivePostChange,
}) => {
    const itemNodesRef = useRef(new Map());
    const activeIdRef = useRef(null);
    const safePosts = Array.isArray(posts) ? posts : [];

    const setItemRef = useCallback((postId) => (node) => {
        if (!postId) {
            return;
        }
        if (node) {
            itemNodesRef.current.set(postId, node);
        } else {
            itemNodesRef.current.delete(postId);
        }
    }, []);

    useEffect(() => {
        if (safePosts.length === 0) {
            if (activeIdRef.current !== null) {
                activeIdRef.current = null;
                onActivePostChange?.(null);
            }
            return;
        }

        const activePostIsVisible = safePosts.some(
            post => String(post?._id) === String(activeIdRef.current)
        );

        if (!activePostIsVisible) {
            const nextActiveId = safePosts[0]?._id ?? null;
            activeIdRef.current = nextActiveId;
            onActivePostChange?.(nextActiveId);
        }
    }, [safePosts, onActivePostChange]);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
            return undefined;
        }

        if (safePosts.length === 0) {
            if (activeIdRef.current !== null) {
                activeIdRef.current = null;
                onActivePostChange?.(null);
            }
            return undefined;
        }

        const centerBandMargin = '-45% 0px -45% 0px';

        const observer = new IntersectionObserver(
            (entries) => {
                const intersecting = entries.filter(entry => entry.isIntersecting);
                if (intersecting.length === 0) {
                    return;
                }

                const viewportCenterY = window.innerHeight / 2;
                let bestId = null;
                let bestDistance = Number.POSITIVE_INFINITY;

                intersecting.forEach((entry) => {
                    const rect = entry.boundingClientRect;
                    const centerY = rect.top + rect.height / 2;
                    const distance = Math.abs(centerY - viewportCenterY);
                    const id = entry.target?.getAttribute?.('data-post-id');

                    if (id && distance < bestDistance) {
                        bestDistance = distance;
                        bestId = id;
                    }
                });

                if (bestId && bestId !== activeIdRef.current) {
                    activeIdRef.current = bestId;
                    onActivePostChange?.(bestId);
                }
            },
            { root: null, rootMargin: centerBandMargin, threshold: 0 }
        );

        itemNodesRef.current.forEach((node) => observer.observe(node));

        return () => {
            observer.disconnect();
        };
    }, [safePosts, onActivePostChange]);

    if (loading) {
        return <div className="loading">[ACCESSING DATABASE...]</div>;
    }

    if (safePosts.length === 0) {
        return (
            <div className="no-posts">
                [No Documents Match Chosen Tags]
            </div>
        );
    }

    return (
        <>
            <div className="post-list">
                {safePosts.map(post => (
                    <PostItem
                        key={post._id}
                        post={post}
                        selectedTags={selectedTags}
                        selectedTagSet={selectedTagSet}
                        onReadMore={onSelectPost}
                        ref={setItemRef(post._id)}
                    />
                ))}
            </div>
            {totalPages > 1 && (
                <nav className="pagination" aria-label="Document pages">
                    <button
                        type="button"
                        className="pagination__button"
                        onClick={() => onPageChange?.(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>
                    <div className="pagination__pages" role="list">
                        {Array.from({ length: totalPages }, (_, index) => {
                            const pageNumber = index + 1;
                            const isActive = pageNumber === currentPage;

                            return (
                                <button
                                    key={pageNumber}
                                    type="button"
                                    className={`pagination__button pagination__button--page${isActive ? ' active' : ''}`}
                                    aria-current={isActive ? 'page' : undefined}
                                    onClick={() => onPageChange?.(pageNumber)}
                                >
                                    {pageNumber}
                                </button>
                            );
                        })}
                    </div>
                    <button
                        type="button"
                        className="pagination__button"
                        onClick={() => onPageChange?.(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </button>
                </nav>
            )}
        </>
    );
};

export default PostList;
